import React from 'react';
import { Box } from '@mui/material';

import { INDETERMINATE, PropertyTextInput } from '@paragon/client-tools';
import { HeelSettings } from '@paragon/contract-library';
import { Direction2D, distanceGreaterThan, distanceLessThan } from '@paragon/geometry-library';
import { DistanceField } from '@paragon/material';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import {
  getBottomChordWidthDuringProfileInsertion,
  getTopChordWidthDuringProfileInsertion,
} from '../../../../../../store/selectors/profileSelectors';
import { useAppSelector } from '../../../../../hooks';

interface Props {
  readonly heelSettings: HeelSettings;
  readonly topChordSlopeInDegrees: number;
  readonly onChange: (heelSettings: HeelSettings) => void;
}

export function ShapeDetailsHeelSettings({ heelSettings, topChordSlopeInDegrees, onChange }: Props) {
  const topChordWidth = useAppSelector(getTopChordWidthDuringProfileInsertion);
  const bottomChordWidth = useAppSelector(getBottomChordWidthDuringProfileInsertion);

  const { heelHeight, buttCut } = calculatedHeelHeightAndButtCut(heelSettings, topChordWidth, topChordSlopeInDegrees);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 1, justifyContent: 'space-evenly' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <DistanceField
          label="Heel Height"
          value={heelHeight}
          onSubmit={(newHeelHeight) => {
            onChange({ ...heelSettings, heelMethod: 'HeelHeight', heelHeight: newHeelHeight });
          }}
          sx={{ m: 1 }}
          min={1.0 / 16.0}
        />
        {distanceGreaterThan(buttCut, bottomChordWidth) || distanceLessThan(buttCut, 0) ? (
          <PropertyTextInput property="Butt Cut" value="N/A" disabled sx={{ m: 1 }} />
        ) : (
          <DistanceField
            label="Butt Cut"
            canBeIndeterminate
            value={distanceGreaterThan(buttCut, bottomChordWidth) ? INDETERMINATE : buttCut}
            onSubmit={async (newButtCut) => {
              onChange({ ...heelSettings, heelMethod: 'ButtCut', buttCut: newButtCut });
            }}
            sx={{ m: 1 }}
            min={0}
          />
        )}
      </Box>
    </Box>
  );
}

function calculatedHeelHeightAndButtCut(
  heelSettings: HeelSettings,
  topChordWidth: number,
  topChordSlopeInDegrees: number,
) {
  const topChordVerticalWidth = getMemberVerticalWidth(topChordWidth, Direction2D.fromAngle(topChordSlopeInDegrees));

  switch (heelSettings.heelMethod) {
    case 'HeelHeight':
      return { heelHeight: heelSettings.heelHeight, buttCut: heelSettings.heelHeight - topChordVerticalWidth };
    case 'ButtCut':
      return { buttCut: heelSettings.buttCut, heelHeight: heelSettings.buttCut + topChordVerticalWidth };
    default:
      assertExhaustiveSwitch(heelSettings);
  }
}

function getMemberVerticalWidth(memberWidth: number, memberDirection: Direction2D) {
  return memberWidth / Math.abs(memberDirection.x);
}
